import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Layout/Header';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, Edit2, CheckCircle, XCircle, Download, 
  User, Briefcase, Building2, CreditCard, Users, 
  GraduationCap, FileText, MapPin, Shield, Clock,
  Phone, Mail, Calendar, Hash, AlertTriangle, Heart
} from 'lucide-react';

const EmployeeMasterView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [insurance, setInsurance] = useState({ policies: [], total_policies: 0 });
  const [insuranceLoading, setInsuranceLoading] = useState(false);

  useEffect(() => {
    fetchEmployee();
    fetchDepartments();
  }, [id]);

  useEffect(() => {
    if (employee) {
      fetchInsurance();
    }
  }, [employee]);

  const fetchEmployee = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/employee-master/${id}`);
      setEmployee(res.data);
    } catch (error) {
      toast.error('Failed to fetch employee');
      navigate('/employee-master');
    } finally {
      setLoading(false);
    }
  };

  const fetchInsurance = async () => {
    setInsuranceLoading(true);
    try {
      const res = await api.get(`/insurance/employee/${id}`);
      setInsurance(res.data);
    } catch (error) {
      console.error('Failed to fetch insurance');
      setInsurance({ policies: [], total_policies: 0 });
    } finally {
      setInsuranceLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await api.get('/departments');
      setDepartments(res.data || []);
    } catch (error) {
      console.error('Failed to fetch departments');
      setDepartments([]);
    }
  };

  const getDepartmentName = (id) => {
    if (!id) return '-';
    const dept = departments.find(d => d.id === id);
    return dept?.name || id;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!employee) return null;

  return (
    <div data-testid="employee-master-view">
      <Header 
        title={
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/employee-master')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <span>Employee Profile</span>
          </div>
        }
        subtitle={`${employee.employee_code} - ${employee.name}`}
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => window.print()}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button onClick={() => navigate(`/employee-master/${id}/edit`)}>
              <Edit2 className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </div>
        }
      />

      <div className="p-6">
        {/* Profile Header Card */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 mb-6 text-white">
          <div className="flex items-start gap-6">
            <div className="w-24 h-24 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center text-4xl font-bold">
              {employee.name?.charAt(0) || '?'}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-2xl font-bold">{employee.name}</h2>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  employee.authorization_status === 'authorized' 
                    ? 'bg-emerald-400/20 text-emerald-100' 
                    : 'bg-amber-400/20 text-amber-100'
                }`}>
                  {employee.authorization_status?.toUpperCase()}
                </span>
              </div>
              <p className="text-blue-100 mb-4">{employee.designation} • {getDepartmentName(employee.department_id)}</p>
              <div className="grid grid-cols-4 gap-6">
                <div>
                  <p className="text-blue-200 text-xs">Employee Code</p>
                  <p className="font-medium">{employee.employee_code}</p>
                </div>
                <div>
                  <p className="text-blue-200 text-xs">Grade</p>
                  <p className="font-medium">{employee.grade || '-'}</p>
                </div>
                <div>
                  <p className="text-blue-200 text-xs">Date of Joining</p>
                  <p className="font-medium">{formatDate(employee.date_of_joining)}</p>
                </div>
                <div>
                  <p className="text-blue-200 text-xs">Employment Type</p>
                  <p className="font-medium capitalize">{employee.employment_type}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="personal" className="space-y-6">
          <TabsList className="bg-white border rounded-lg p-1 flex-wrap h-auto">
            <TabsTrigger value="personal" className="gap-2">
              <User className="w-4 h-4" /> Personal
            </TabsTrigger>
            <TabsTrigger value="employment" className="gap-2">
              <Briefcase className="w-4 h-4" /> Employment
            </TabsTrigger>
            <TabsTrigger value="family" className="gap-2">
              <Users className="w-4 h-4" /> Family & Nominees
            </TabsTrigger>
            <TabsTrigger value="education" className="gap-2">
              <GraduationCap className="w-4 h-4" /> Education
            </TabsTrigger>
            <TabsTrigger value="bank" className="gap-2">
              <CreditCard className="w-4 h-4" /> Bank & Statutory
            </TabsTrigger>
            <TabsTrigger value="addresses" className="gap-2">
              <MapPin className="w-4 h-4" /> Addresses
            </TabsTrigger>
            <TabsTrigger value="documents" className="gap-2">
              <FileText className="w-4 h-4" /> Documents
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <Clock className="w-4 h-4" /> History
            </TabsTrigger>
            <TabsTrigger value="insurance" className="gap-2">
              <Heart className="w-4 h-4" /> Insurance
              {insurance.total_policies > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">{insurance.total_policies}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Personal Tab */}
          <TabsContent value="personal">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border p-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <InfoField label="First Name" value={employee.first_name} />
                  <InfoField label="Middle Name" value={employee.middle_name} />
                  <InfoField label="Last Name" value={employee.last_name} />
                  <InfoField label="Date of Birth" value={formatDate(employee.date_of_birth)} />
                  <InfoField label="Gender" value={employee.gender} />
                  <InfoField label="Blood Group" value={employee.blood_group} />
                  <InfoField label="Marital Status" value={employee.marital_status} />
                  <InfoField label="Marriage Date" value={formatDate(employee.marriage_date)} />
                  <InfoField label="Father's Name" value={employee.father_name} />
                  <InfoField label="Mother's Name" value={employee.mother_name} />
                  <InfoField label="Spouse Name" value={employee.spouse_name} />
                  <InfoField label="Nationality" value={employee.nationality} />
                  <InfoField label="Religion" value={employee.religion} />
                  <InfoField label="Caste" value={employee.caste} />
                  <InfoField label="Category" value={employee.special_category_type} />
                </div>
              </div>

              <div className="bg-white rounded-xl border p-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Phone className="w-5 h-5 text-blue-600" />
                  Contact Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <InfoField label="Primary Mobile" value={employee.mobile_primary} />
                  <InfoField label="Secondary Mobile" value={employee.mobile_secondary} />
                  <InfoField label="Office Phone" value={employee.phone_office} />
                  <InfoField label="Residence Phone" value={employee.phone_residence} />
                  <InfoField label="Official Email" value={employee.email} />
                  <InfoField label="Personal Email" value={employee.personal_email} />
                </div>
                
                <h3 className="font-semibold text-slate-900 mt-6 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  Identification
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <InfoField label="Aadhaar Number" value={employee.aadhaar_number} masked />
                  <InfoField label="PAN Number" value={employee.pan_number} />
                  <InfoField label="Passport Number" value={employee.passport_number} />
                  <InfoField label="Passport Expiry" value={formatDate(employee.passport_expiry_date)} />
                  <InfoField label="Driving License" value={employee.driving_license_number} />
                  <InfoField label="Voter ID" value={employee.voter_id} />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Employment Tab */}
          <TabsContent value="employment">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border p-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                  Appointment Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <InfoField label="Date of Appointment" value={formatDate(employee.date_of_appointment)} />
                  <InfoField label="Date of Joining" value={formatDate(employee.date_of_joining)} />
                  <InfoField label="Appointment Type" value={employee.appointment_type} />
                  <InfoField label="Employment Type" value={employee.employment_type} />
                  <InfoField label="Employee Group" value={employee.employee_group} />
                  <InfoField label="Cadre" value={employee.cadre} />
                  <InfoField label="File Number" value={employee.file_number} />
                </div>

                <h3 className="font-semibold text-slate-900 mt-6 mb-4">Probation Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <InfoField label="On Probation" value={employee.is_on_probation ? 'Yes' : 'No'} />
                  <InfoField label="Probation Period" value={employee.probation_period_months ? `${employee.probation_period_months} months` : '-'} />
                  <InfoField label="Probation Start" value={formatDate(employee.probation_start_date)} />
                  <InfoField label="Probation End" value={formatDate(employee.probation_end_date)} />
                  <InfoField label="Confirmation Date" value={formatDate(employee.confirmation_date)} />
                  <InfoField label="Regularization Date" value={formatDate(employee.date_of_regularization)} />
                </div>
              </div>

              <div className="bg-white rounded-xl border p-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Current Posting
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <InfoField label="Department" value={getDepartmentName(employee.department_id)} />
                  <InfoField label="Designation" value={employee.designation} />
                  <InfoField label="Grade" value={employee.grade} />
                  <InfoField label="Pay Level" value={employee.pay_level} />
                  <InfoField label="Pay Scale" value={employee.pay_scale} />
                  <InfoField label="Basic Pay" value={employee.basic_pay ? `₹${employee.basic_pay.toLocaleString()}` : '-'} />
                </div>

                <h3 className="font-semibold text-slate-900 mt-6 mb-4">Retirement Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <InfoField label="Date of Retirement" value={formatDate(employee.date_of_retirement)} />
                  <InfoField label="Retirement Type" value={employee.retirement_type || 'Superannuation'} />
                </div>

                {(employee.has_pending_vigilance || employee.has_police_case) && (
                  <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200">
                    <h4 className="font-medium text-red-700 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Alerts
                    </h4>
                    {employee.has_pending_vigilance && (
                      <p className="text-sm text-red-600 mt-2">• Pending vigilance case</p>
                    )}
                    {employee.has_police_case && (
                      <p className="text-sm text-red-600 mt-1">• Police case on record</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Family & Nominees Tab */}
          <TabsContent value="family">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Family Members</h3>
                {employee.family_members?.length > 0 ? (
                  <div className="space-y-4">
                    {employee.family_members.map((member) => (
                      <div key={member.name + member.relation} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium">{member.name}</p>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded capitalize">
                            {member.relation}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
                          <p>DOB: {formatDate(member.date_of_birth)}</p>
                          <p>Dependent: {member.is_dependent ? 'Yes' : 'No'}</p>
                          <p>Occupation: {member.occupation || '-'}</p>
                          <p>Govt Employee: {member.is_employed_in_govt ? 'Yes' : 'No'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No family members added</p>
                )}
              </div>

              <div className="bg-white rounded-xl border p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Nominees</h3>
                {employee.nominees?.length > 0 ? (
                  <div className="space-y-4">
                    {employee.nominees.map((nominee) => (
                      <div key={nominee.name + nominee.nominee_type} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium">{nominee.name}</p>
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">
                            {nominee.percentage}%
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
                          <p>Relation: <span className="capitalize">{nominee.relation}</span></p>
                          <p>Type: <span className="capitalize">{nominee.nominee_type}</span></p>
                          <p>Minor: {nominee.is_minor ? 'Yes' : 'No'}</p>
                          {nominee.is_minor && <p>Guardian: {nominee.guardian_name || '-'}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No nominees added</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Education Tab */}
          <TabsContent value="education">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Educational Qualifications</h3>
                {employee.education_qualifications?.length > 0 ? (
                  <div className="space-y-4">
                    {employee.education_qualifications.map((edu) => (
                      <div key={edu.qualification + edu.institution} className="p-4 bg-slate-50 rounded-lg">
                        <p className="font-medium">{edu.qualification}</p>
                        <p className="text-sm text-slate-600">{edu.specialization}</p>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-slate-600">
                          <p>Institution: {edu.institution}</p>
                          <p>University: {edu.university_board}</p>
                          <p>Year: {edu.year_of_passing}</p>
                          <p>Percentage: {edu.percentage_cgpa || '-'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No qualifications added</p>
                )}
              </div>

              <div className="bg-white rounded-xl border p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Previous Experience</h3>
                {employee.previous_experience?.length > 0 ? (
                  <div className="space-y-4">
                    {employee.previous_experience.map((exp) => (
                      <div key={exp.organization + exp.from_date} className="p-4 bg-slate-50 rounded-lg">
                        <p className="font-medium">{exp.designation}</p>
                        <p className="text-sm text-slate-600">{exp.organization}</p>
                        <div className="mt-2 text-sm text-slate-600">
                          <p>{formatDate(exp.from_date)} - {formatDate(exp.to_date)}</p>
                          <p>Department: {exp.department || '-'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No experience records</p>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Bank & Statutory Tab */}
          <TabsContent value="bank">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Bank Accounts</h3>
                {employee.bank_accounts?.length > 0 ? (
                  <div className="space-y-4">
                    {employee.bank_accounts.map((account) => (
                      <div key={account.account_number} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-medium">{account.bank_name}</p>
                          {account.is_primary && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Primary</span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm text-slate-600">
                          <p>Account: {account.account_number}</p>
                          <p>IFSC: {account.ifsc_code}</p>
                          <p>Branch: {account.branch_name}</p>
                          <p>Type: <span className="capitalize">{account.account_type}</span></p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No bank accounts added</p>
                )}
              </div>

              <div className="bg-white rounded-xl border p-6">
                <h3 className="font-semibold text-slate-900 mb-4">Statutory Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <InfoField label="PF Number" value={employee.epf_number} />
                  <InfoField label="UAN" value={employee.uan_number} />
                  <InfoField label="GPF Number" value={employee.gpf_number} />
                  <InfoField label="PRAN" value={employee.pran_number} />
                  <InfoField label="ESI Number" value={employee.esi_number} />
                  <InfoField label="PFMS Number" value={employee.pfms_number} />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Addresses Tab */}
          <TabsContent value="addresses">
            <div className="bg-white rounded-xl border p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Addresses</h3>
              {employee.addresses?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {employee.addresses.map((addr) => (
                    <div key={addr.address_type + addr.pin_code} className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-4 h-4 text-blue-600" />
                        <span className="font-medium capitalize">{addr.address_type} Address</span>
                      </div>
                      <p className="text-sm text-slate-600">
                        {addr.address_line1}
                        {addr.address_line2 && <>, {addr.address_line2}</>}
                        {addr.address_line3 && <>, {addr.address_line3}</>}
                      </p>
                      <p className="text-sm text-slate-600 mt-1">
                        {addr.city}, {addr.district && `${addr.district}, `}{addr.state} - {addr.pin_code}
                      </p>
                      <p className="text-sm text-slate-600">{addr.country}</p>
                      <div className="mt-2 flex gap-2">
                        {addr.is_metro && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">Metro</span>}
                        {addr.is_home_state && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded">Home State</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No addresses added</p>
              )}
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <div className="bg-white rounded-xl border p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Documents Repository</h3>
              {employee.documents?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {employee.documents.map((doc) => (
                    <div key={doc.document_name + doc.document_type} className="p-4 border rounded-lg hover:border-blue-300 transition-colors">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-sm capitalize">{doc.document_type.replace('_', ' ')}</span>
                      </div>
                      <p className="text-xs text-slate-600 truncate">{doc.document_name}</p>
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-xs text-slate-400">{formatDate(doc.uploaded_at)}</span>
                        {doc.is_verified && (
                          <CheckCircle className="w-4 h-4 text-emerald-600" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No documents uploaded</p>
              )}
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <div className="bg-white rounded-xl border p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Posting History</h3>
              {employee.posting_history?.length > 0 ? (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200"></div>
                  <div className="space-y-6">
                    {employee.posting_history.map((posting) => (
                      <div key={posting.from_date + posting.office_name} className="relative pl-10">
                        <div className="absolute left-2 top-1 w-5 h-5 rounded-full bg-blue-600 border-4 border-white"></div>
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium">{posting.designation}</p>
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded capitalize">
                              {posting.transfer_type}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600">{posting.office_name}</p>
                          <p className="text-sm text-slate-600">{posting.department}</p>
                          <p className="text-xs text-slate-500 mt-2">
                            {formatDate(posting.from_date)} - {posting.to_date ? formatDate(posting.to_date) : 'Present'}
                          </p>
                          {posting.order_number && (
                            <p className="text-xs text-slate-500">Order: {posting.order_number} ({formatDate(posting.order_date)})</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No posting history</p>
              )}
            </div>
          </TabsContent>

          {/* Insurance Tab */}
          <TabsContent value="insurance">
            <div className="bg-white rounded-xl border p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-500" />
                  Insurance Policies
                </h3>
                {employee.health_card_number && (
                  <div className="flex items-center gap-2 bg-green-50 px-3 py-1 rounded-lg">
                    <Shield className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700">Health Card: {employee.health_card_number}</span>
                  </div>
                )}
              </div>

              {insuranceLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : insurance.policies?.length > 0 ? (
                <div className="space-y-4">
                  {insurance.policies.map((policy) => (
                    <div 
                      key={policy.id || policy.policy_number} 
                      className={`border rounded-xl p-5 ${
                        policy.is_expired 
                          ? 'bg-red-50 border-red-200' 
                          : policy.days_until_expiry && policy.days_until_expiry <= 30 
                            ? 'bg-amber-50 border-amber-200' 
                            : 'bg-green-50 border-green-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-lg">{policy.policy_type || 'Health Insurance'}</h4>
                            <Badge 
                              className={
                                policy.is_expired 
                                  ? 'bg-red-100 text-red-700' 
                                  : policy.days_until_expiry && policy.days_until_expiry <= 30 
                                    ? 'bg-amber-100 text-amber-700' 
                                    : 'bg-green-100 text-green-700'
                              }
                            >
                              {policy.is_expired ? 'Expired' : policy.days_until_expiry <= 30 ? 'Expiring Soon' : 'Active'}
                            </Badge>
                          </div>
                          <p className="text-slate-600">{policy.provider || 'Health Serve'}</p>
                        </div>
                        {policy.sum_insured > 0 && (
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Sum Insured</p>
                            <p className="font-bold text-lg text-slate-800">₹{policy.sum_insured?.toLocaleString('en-IN')}</p>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-slate-500">Policy Number</p>
                          <p className="font-medium text-sm">{policy.policy_number || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Health Card No.</p>
                          <p className="font-medium text-sm">{policy.health_card_number || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Valid From</p>
                          <p className="font-medium text-sm">{policy.valid_from ? formatDate(policy.valid_from) : '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Valid Till</p>
                          <p className="font-medium text-sm">{policy.valid_till ? formatDate(policy.valid_till) : '-'}</p>
                        </div>
                      </div>

                      {!policy.is_expired && policy.days_until_expiry !== null && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <p className="text-sm">
                            {policy.days_until_expiry > 0 ? (
                              <span className={policy.days_until_expiry <= 30 ? 'text-amber-700' : 'text-green-700'}>
                                {policy.days_until_expiry} days until expiry
                              </span>
                            ) : (
                              <span className="text-red-600">Policy has expired</span>
                            )}
                          </p>
                        </div>
                      )}

                      {policy.beneficiary_name && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <p className="text-xs text-slate-500">Beneficiary</p>
                          <p className="font-medium text-sm">
                            {policy.beneficiary_name} ({policy.beneficiary_relationship})
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Heart className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-500">No insurance policies found for this employee</p>
                  <p className="text-sm text-slate-400 mt-1">Insurance policies can be added through the Insurance module</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Helper component for displaying info fields
const InfoField = ({ label, value, masked }) => (
  <div>
    <p className="text-xs text-slate-500">{label}</p>
    <p className="font-medium text-slate-900">
      {masked && value ? `XXXX${value.slice(-4)}` : value || '-'}
    </p>
  </div>
);

export default EmployeeMasterView;
